import { useState, useEffect, useRef, useCallback } from "react"
import {
  Bold, Italic, Underline as UnderlineIcon,
  Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight,
  Code2, Eye, MonitorPlay, Palette, Type, List, ListOrdered,
} from "lucide-react"

interface Props {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: string
}

function ToolbarBtn({ onClick, active, children, title, disabled }: { onClick: () => void; active?: boolean; children: React.ReactNode; title: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded transition-colors ${disabled ? "opacity-30 cursor-not-allowed" : ""} ${active ? "bg-blue-500 text-white" : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white"}`}
    >
      {children}
    </button>
  )
}

function Sep() {
  return <div className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-0.5" />
}

export default function HtmlEditor({ value, onChange, placeholder = "Escreva o conteúdo...", minHeight = "250px" }: Props) {
  const [mode, setMode] = useState<"visual" | "code" | "preview">("code")
  const [codeValue, setCodeValue] = useState(value || "")
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const previewRef = useRef<HTMLIFrameElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const visualSyncRef = useRef(false)

  // Sync external value
  useEffect(() => { setCodeValue(value || "") }, [value])

  // Setup visual iframe (editable)
  const setupVisualIframe = useCallback(() => {
    if (!iframeRef.current) return
    const doc = iframeRef.current.contentDocument
    if (!doc) return

    doc.open()
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 16px; margin: 0; font-size: 14px; line-height: 1.6; color: #333; background: #fff; min-height: ${minHeight}; }
      body:empty::before { content: "${placeholder}"; color: #aaa; pointer-events: none; }
      img { max-width: 100%; height: auto; cursor: pointer; }
      img:focus, img.selected { outline: 2px solid #005092; outline-offset: 2px; }
      table { border-collapse: collapse; width: 100%; }
      td, th { padding: 8px; }
      a { color: #005092; }
      ::selection { background: #bfdbfe; }
    </style></head><body contenteditable="true">${codeValue}</body></html>`)
    doc.close()

    // Listen for changes
    doc.body.addEventListener("input", () => {
      if (visualSyncRef.current) return
      const html = doc.body.innerHTML
      setCodeValue(html)
      onChange(html)
    })

    // Make images clickable/selectable
    doc.body.addEventListener("click", (e: Event) => {
      const target = e.target as HTMLElement
      if (target.tagName === "IMG") {
        doc.querySelectorAll("img.selected").forEach(i => i.classList.remove("selected"))
        target.classList.add("selected")
        target.focus()
      }
    })
  }, [codeValue, onChange, placeholder, minHeight])

  // Setup visual mode
  useEffect(() => {
    if (mode === "visual") {
      // Small delay to ensure iframe is mounted
      const t = setTimeout(setupVisualIframe, 50)
      return () => clearTimeout(t)
    }
  }, [mode, setupVisualIframe])

  // Setup preview iframe (read-only)
  useEffect(() => {
    if (mode === "preview" && previewRef.current) {
      const doc = previewRef.current.contentDocument
      if (!doc) return
      doc.open()
      doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 16px; margin: 0; font-size: 14px; line-height: 1.6; color: #333; background: #fff; }
        img { max-width: 100%; height: auto; }
        table { border-collapse: collapse; }
        td, th { padding: 8px; }
        a { color: #005092; }
      </style></head><body>${codeValue}</body></html>`)
      doc.close()
      setTimeout(() => {
        if (previewRef.current?.contentDocument) {
          const h = previewRef.current.contentDocument.body.scrollHeight
          previewRef.current.style.height = Math.max(parseInt(minHeight), h + 32) + "px"
        }
      }, 100)
    }
  }, [mode, codeValue, minHeight])

  // Execute command on visual iframe
  function execCmd(cmd: string, val?: string) {
    if (mode !== "visual" || !iframeRef.current?.contentDocument) return
    iframeRef.current.contentDocument.execCommand(cmd, false, val)
    // Sync after command
    setTimeout(() => {
      const html = iframeRef.current?.contentDocument?.body.innerHTML || ""
      setCodeValue(html)
      onChange(html)
    }, 10)
  }

  function switchToVisual() {
    setMode("visual")
  }

  function switchToCode() {
    // Read latest from visual iframe if coming from visual
    if (mode === "visual" && iframeRef.current?.contentDocument) {
      const html = iframeRef.current.contentDocument.body.innerHTML
      setCodeValue(html)
    }
    setMode("code")
  }

  function switchToPreview() {
    if (mode === "visual" && iframeRef.current?.contentDocument) {
      const html = iframeRef.current.contentDocument.body.innerHTML
      setCodeValue(html)
    }
    setMode("preview")
  }

  function handleCodeChange(newCode: string) {
    setCodeValue(newCode)
    onChange(newCode)
  }

  function insertAtCursor(text: string) {
    if (mode === "code" && textareaRef.current) {
      const ta = textareaRef.current
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const newVal = codeValue.substring(0, start) + text + codeValue.substring(end)
      setCodeValue(newVal)
      onChange(newVal)
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + text.length }, 0)
    } else if (mode === "visual" && iframeRef.current?.contentDocument) {
      iframeRef.current.contentDocument.execCommand("insertHTML", false, text)
    }
  }

  function insertImage() {
    const url = prompt("URL da imagem:")
    if (!url) return
    const width = prompt("Largura (px):", "300")
    const tag = `<img src="${url}" width="${width || 300}" style="max-width:100%;width:${width || 300}px;" alt="" />`
    insertAtCursor(tag)
  }

  function insertLink() {
    if (mode === "visual") {
      const url = prompt("URL do link:", "https://")
      if (url) execCmd("createLink", url)
    } else {
      const url = prompt("URL do link:", "https://")
      const text = prompt("Texto do link:", "Clique aqui")
      if (url && text) insertAtCursor(`<a href="${url}" style="color:#005092;">${text}</a>`)
    }
  }

  function insertColor() {
    const color = prompt("Cor (hex):", "#005092")
    if (color) execCmd("foreColor", color)
  }

  // Expose for parent (variable insertion from glossary)
  useEffect(() => {
    (window as any).__htmlEditorInsert = insertAtCursor
    return () => { delete (window as any).__htmlEditorInsert }
  })

  const s = 14
  const isVisual = mode === "visual"
  const isCode = mode === "code"
  const isPreview = mode === "preview"

  return (
    <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden bg-white dark:bg-zinc-800">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50">
        <div className="flex flex-wrap items-center gap-0.5 p-1.5">
          <ToolbarBtn onClick={() => execCmd("bold")} title="Negrito" disabled={!isVisual}><Bold size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("italic")} title="Itálico" disabled={!isVisual}><Italic size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("underline")} title="Sublinhado" disabled={!isVisual}><UnderlineIcon size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={insertColor} title="Cor do texto" disabled={!isVisual}><Palette size={s} /></ToolbarBtn>
          <Sep />
          <ToolbarBtn onClick={() => execCmd("formatBlock", "p")} title="Parágrafo" disabled={!isVisual}><Type size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("formatBlock", "h1")} title="Título 1" disabled={!isVisual}><span style={{fontSize:12,fontWeight:800}}>H1</span></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("formatBlock", "h2")} title="Título 2" disabled={!isVisual}><span style={{fontSize:12,fontWeight:800}}>H2</span></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("formatBlock", "h3")} title="Título 3" disabled={!isVisual}><span style={{fontSize:12,fontWeight:800}}>H3</span></ToolbarBtn>
          <Sep />
          <ToolbarBtn onClick={() => execCmd("insertUnorderedList")} title="Lista" disabled={!isVisual}><List size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("insertOrderedList")} title="Lista Numerada" disabled={!isVisual}><ListOrdered size={s} /></ToolbarBtn>
          <Sep />
          <ToolbarBtn onClick={() => execCmd("justifyLeft")} title="Esquerda" disabled={!isVisual}><AlignLeft size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("justifyCenter")} title="Centro" disabled={!isVisual}><AlignCenter size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={() => execCmd("justifyRight")} title="Direita" disabled={!isVisual}><AlignRight size={s} /></ToolbarBtn>
          <Sep />
          <ToolbarBtn onClick={insertLink} title="Link" disabled={isPreview}><LinkIcon size={s} /></ToolbarBtn>
          <ToolbarBtn onClick={insertImage} title="Imagem" disabled={isPreview}><ImageIcon size={s} /></ToolbarBtn>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 pr-2 flex-shrink-0">
          <button type="button" onClick={mode !== "visual" ? switchToVisual : undefined}
            className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${isVisual ? "bg-blue-500 text-white" : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700"}`}>
            <Eye size={12} /> Visual
          </button>
          <button type="button" onClick={mode !== "code" ? switchToCode : undefined}
            className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${isCode ? "bg-blue-500 text-white" : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700"}`}>
            <Code2 size={12} /> Código
          </button>
          <button type="button" onClick={switchToPreview}
            className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${isPreview ? "bg-blue-500 text-white" : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700"}`}>
            <MonitorPlay size={12} /> Preview
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ minHeight }}>
        {isVisual && (
          <iframe
            ref={iframeRef}
            title="Editor Visual"
            className="w-full border-none"
            style={{ minHeight, height: minHeight }}
          />
        )}
        {isCode && (
          <textarea
            ref={textareaRef}
            value={codeValue}
            onChange={e => handleCodeChange(e.target.value)}
            spellCheck={false}
            className="w-full h-full px-4 py-3 text-sm font-mono leading-relaxed bg-zinc-950 text-green-400 focus:outline-none resize-y border-none"
            style={{ minHeight }}
            placeholder="Cole ou escreva HTML aqui..."
          />
        )}
        {isPreview && (
          <div className="bg-white" style={{ minHeight }}>
            <iframe ref={previewRef} title="Preview" className="w-full border-none" style={{ minHeight }} sandbox="allow-same-origin" />
          </div>
        )}
      </div>
    </div>
  )
}
